<template>
  <modal-component
    :active="active"
    @cancel="close"
    @hdlOnClick="downloadGuide"
    textSave="Descargar"
    titleModal="Guías de llenado"
    :disabled="loading || !selectedGuide"
    :loader="loading"
    widthModal="width: 90%; max-width: 1200px;"
  >
    <div class="guide-content">
      <div class="guide-sidebar">
        <div class="field">
          <div class="control">
            <div class="select is-fullwidth">
              <select v-model="selectedGuide">
                <option :value="null" disabled>Seleccione una opción</option>
                <option value="html">Html (.html)</option>
                <option value="docx">Word (.docx)</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div class="guide-preview">
        <div v-if="!selectedGuide" class="empty-state">
          <div class="empty-icon">
            <i class="fas fa-file-pdf"></i>
          </div>
          <p>Selecciona un tipo de guía para ver la vista previa</p>
        </div>

        <div v-else class="pdf-preview">
          <div class="pdf-viewer">
            <iframe
              v-show="!pdfLoading && !pdfError"
              :src="getPdfUrl()"
              width="100%"
              height="100%"
              frameborder="0"
              @load="onPdfLoad"
              @error="onPdfError"
            >
            </iframe>

            <div v-if="pdfLoading" class="pdf-loading">
              <a :class="{'is-loading': pdfLoading}" :disabled="loading"></a>
            </div>

            <div v-if="pdfError" class="pdf-error">
              <div class="error-icon">
                <i class="fas fa-exclamation-triangle"></i>
              </div>
              <p>No se pudo cargar la vista previa del PDF</p>
              <small>El archivo se descargará correctamente</small>
            </div>
          </div>
        </div>
      </div>

      <!-- Error message -->
      <div v-if="errorMessage" class="notification is-danger is-light error-notification">
        <button class="delete" @click="errorMessage = null"></button>
        <strong>Error:</strong> {{ errorMessage }}
      </div>
    </div>
  </modal-component>
</template>

<script>
export default {
  name: 'ModalDownloadGuide',
  components: {
    ModalComponent: () => import('@/components/ui/ModalComponent')
  },
  props: {
    active: {
      type: Boolean,
      default: false
    },
    close: {
      type: Function,
      default: () => {}
    },
    preSelectedType: {
      type: String,
      default: null,
      validator: (value) => value === null || ['html', 'docx'].includes(value)
    }
  },
  data () {
    return {
      loading: false,
      selectedGuide: null,
      errorMessage: null,
      pdfLoading: false,
      pdfError: false
    }
  },
  methods: {
    async downloadGuide () {
      if (!this.selectedGuide) return

      this.loading = true
      this.errorMessage = null

      try {
        const fileName = this.getFileName()
        const pdfPath = this.getPdfPath()

        const response = await fetch(pdfPath)

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)

        const link = document.createElement('a')
        link.href = url
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        window.URL.revokeObjectURL(url)
      } catch (error) {
      } finally {
        this.loading = false
      }
    },

    getPdfPath () {
      const paths = {
        html: '/Handlebars.pdf',
        docx: '/DocxTemplater.pdf'
      }
      return paths[this.selectedGuide] || ''
    },

    getPdfUrl () {
      const basePath = this.getPdfPath()
      return `${basePath}#toolbar=1&navpanes=0&scrollbar=1&page=1&view=FitH`
    },

    onPdfLoad () {
      this.pdfLoading = false
      this.pdfError = false
    },

    onPdfError () {
      this.pdfLoading = false
      this.pdfError = true
    },

    getFileName () {
      const names = {
        html: 'Guia de llenado HTML.pdf',
        docx: 'Guia de llenado Docx.pdf'
      }
      return names[this.selectedGuide] || 'guia-llenado.pdf'
    },

    resetForm () {
      this.errorMessage = null
      this.loading = false
      this.pdfLoading = false
      this.pdfError = false

      if (this.preSelectedType) {
        this.selectedGuide = this.preSelectedType
      } else {
        this.selectedGuide = null
      }
    }
  },

  watch: {
    active (newVal) {
      if (newVal) {
        this.resetForm()
      }
    },

    preSelectedType (newVal) {
      if (newVal && this.active) {
        this.selectedGuide = newVal
      }
    },

    selectedGuide (newVal) {
      if (newVal) {
        this.pdfLoading = true
        this.pdfError = false
      }
    }
  },

  mounted () {
    if (this.preSelectedType && this.active) {
      this.selectedGuide = this.preSelectedType
    }
  }
}
</script>

<style lang="scss" scoped>
.guide-content {
  display: flex;
  gap: 2rem;
  min-height: 500px;
  max-height: 70vh;
  height: 70vh;
  padding: 1rem 0;
}

.guide-sidebar {
  width: 250px;
  flex-shrink: 0;

  .field {
    .select {
      select {
        border: 1px solid #e1e1e1;
        border-radius: 6px;
        padding: 0.75rem;
        font-size: 14px;
        transition: border-color 0.3s ease;
        background: white;

        &:focus {
          border-color: #4f73ff;
          outline: none;
          box-shadow: 0 0 0 3px rgba(79, 115, 255, 0.1);
        }
      }
    }
  }
}

.guide-preview {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  height: 100%;
}

.empty-state {
  text-align: center;
  color: #6c757d;

  .empty-icon {
    font-size: 48px;
    margin-bottom: 1rem;
    opacity: 0.3;
  }

  p {
    font-size: 16px;
    margin: 0;
  }
}

.pdf-preview {
  width: 100%;
  height: 100%;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
}

.pdf-viewer {
  flex: 1;
  position: relative;
  background: #f8f9fa;
  min-height: 400px;

  iframe {
    width: 100%;
    height: 100%;
    display: block;
    border: none;
    background: white;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }
}

.pdf-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(248, 249, 250, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 5;

  .loading-spinner {
    font-size: 32px;
    color: #4f73ff;
    margin-bottom: 1rem;
  }

  p {
    color: #6c757d;
    font-size: 14px;
    margin: 0;
  }
}

.pdf-error {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #f8f9fa;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2rem;
  z-index: 5;

  .error-icon {
    font-size: 48px;
    color: #ffc107;
    margin-bottom: 1rem;
  }

  p {
    color: #495057;
    font-size: 16px;
    font-weight: 500;
    margin-bottom: 0.5rem;
  }

  small {
    color: #6c757d;
    font-size: 14px;
  }
}

.error-notification {
  position: absolute;
  bottom: 1rem;
  left: 1rem;
  right: 1rem;
  border-radius: 6px;
  z-index: 10;

  .delete {
    position: absolute;
    right: 0.5rem;
    top: 0.5rem;
  }

  strong {
    color: #d63384;
  }
}

@media (max-width: 968px) {
  .guide-content {
    flex-direction: column;
    gap: 1rem;
    height: 80vh;
    max-height: 80vh;
    min-height: 600px;
  }

  .guide-sidebar {
    width: 100%;
    flex-shrink: 0;
  }

  .pdf-preview {
    height: 100%;
    flex: 1;
  }

  .pdf-viewer {
    min-height: 500px;
  }
}

@media (max-width: 768px) {
  .guide-content {
    height: 85vh;
    max-height: 85vh;
    min-height: 500px;
    padding: 0.5rem 0;
  }

  .pdf-viewer {
    min-height: 400px;
  }
}

:deep(.modal-card) {
  height: 80vh;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

:deep(.modal-card-body) {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
}
</style>
